export default class LocalStorage {
  constructor (options = {}) {
    this._id = options.id;
    this.attributes = {};
    this._previousAttributes = {};
    this.changed = {};
    this._changing = false;
    this._pending = false;
    this.onChange = (newAttributes) => {
      this.save(newAttributes);
    };
    this.fetch();
  }

  // Fetch data from localStorage and set attributes
  fetch () {
    const data = localStorage.getItem(this._id);
    if (data) {
      this.set(JSON.parse(data));
    }
  }

  // Save data to localStorage
  save (attributes) {
    this.set(attributes);
    localStorage.setItem(this._id, JSON.stringify(this.attributes));
  }

  // Set attributes and trigger change
  set (key, val, options = {}) {
    if (key === null) {
      return this;
    }

    let attrs;

    // Handle both `key, value` and `{key: value}` formats
    if (typeof key === 'object') {
      attrs = key;
      options = val || {};
    } else {
      attrs = {[key]: val};
    }

    // Prepare for attribute changes
    const unset = options.unset || false;
    const silent = options.silent || false;
    const changes = [];
    const current = this.attributes;

    if (!this._changing) {
      this._previousAttributes = {...this.attributes};
      this.changed = {};
    }

    this._changing = true;

    // Apply changes to attributes
    Object.keys(attrs).forEach(attr => {
      const newValue = attrs[attr];
      if (!this._isEqual(current[attr], newValue)) {
        changes.push(attr);
        this.changed[attr] = newValue;
      } else {
        delete this.changed[attr];
      }
      if (unset) {
        delete current[attr];
      } else {
        current[attr] = newValue;
      }
    });

    // Trigger change events
    if (!silent) {
      for (const change of changes) {
        this._trigger(`change:${change}`, current[change], options);
      }
    }

    if (!this._changing) {
      if (!silent) {
        this._pending = true;
        this._trigger('change', options);
        this._pending = false;
      }
      this._changing = false;
    }

    return this;
  }

  // Get the current attributes
  get () {
    return this.attributes;
  }

  // Remove the data from localStorage
  destroy () {
    localStorage.removeItem(this._id);
    this.attributes = {};
  }

  // Check if localStorage is empty
  isEmpty () {
    return Object.keys(this.attributes).length === 0;
  }

  // Clear all attributes
  clear (options = {}) {
    const attrs = Object.keys(this.attributes).reduce((acc, key) => {
      acc[key] = undefined;
      return acc;
    }, {});

    return this.set(attrs, {...options, unset: true});
  }

  // Utility: Trigger event (replace this with your own implementation if needed)
  _trigger (event, ...args) {
    if (typeof this.onChange === 'function') {
      this.onChange(event, ...args);
    }
  }

  // Utility: Deep comparison
  _isEqual (a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
}
